/**
 * Branch Accounts API Client
 * Handles all API calls for bank account management
 */

import axios from '@/lib/api/axios-instance';
import type {
  BranchAccount,
  AccountWithBusinessInfo,
  LinkedBusiness,
  CreateAccountRequest,
  UpdateAccountRequest,
  LinkAccountToBusinessRequest,
  SetPrimaryAccountRequest,
  AccountFilters,
  ApiResponse,
  AccountListResponse,
  AccountWithBusinessListResponse,
  LinkedBusinessListResponse,
  AccountConfigResponse,
} from '@/src/types/account';

const BASE_URL = '/api/v2/business';

// ============================================================================
// Account CRUD Operations
// ============================================================================

export const accountsAPI = {
  /**
   * Get all accounts with optional filters
   */
  async getAll(filters?: AccountFilters): Promise<BranchAccount[]> {
    const params: Record<string, any> = {};
    
    if (filters?.is_active !== undefined) {
      params.is_active = filters.is_active;
    }
    if (filters?.bank_code) {
      params.bank_code = filters.bank_code;
    }
    if (filters?.limit) {
      params.limit = filters.limit;
    }
    
    const response = await axios.get<AccountListResponse>(
      `${BASE_URL}/accounts`,
      { params }
    );
    
    return response.data.data;
  },

  /**
   * Get accounts by branch ID
   */
  async getByBranch(
    branchId: number,
    filters?: Omit<AccountFilters, 'branchId'>
  ): Promise<BranchAccount[]> {
    const params: Record<string, any> = {};
    
    if (filters?.is_active !== undefined) {
      params.is_active = filters.is_active;
    }
    if (filters?.is_primary !== undefined) {
      params.is_primary = filters.is_primary;
    }
    if (filters?.bank_code) {
      params.bank_code = filters.bank_code;
    }
    if (filters?.purpose) {
      params.purpose = filters.purpose;
    }
    if (filters?.limit) {
      params.limit = filters.limit;
    }
    
    const response = await axios.get<AccountListResponse>(
      `${BASE_URL}/branches/${branchId}/accounts`,
      { params }
    );
    
    return response.data.data;
  },

  /**
   * Get accounts by business ID
   */
  async getByBusiness(
    businessId: number,
    filters?: Omit<AccountFilters, 'businessId'>
  ): Promise<AccountWithBusinessInfo[]> {
    const params: Record<string, any> = {};
    
    if (filters?.is_active !== undefined) {
      params.is_active = filters.is_active;
    }
    if (filters?.bank_code) {
      params.bank_code = filters.bank_code;
    }
    
    const response = await axios.get<AccountWithBusinessListResponse>(
      `${BASE_URL}/businesses/${businessId}/accounts`,
      { params }
    );
    
    return response.data.data;
  },

  /**
   * Get single account by ID
   */
  async getById(accountId: number): Promise<BranchAccount> {
    const response = await axios.get<ApiResponse<BranchAccount>>(
      `${BASE_URL}/accounts/${accountId}`
    );
    
    if (!response.data.data) {
      throw new Error('Account not found');
    }
    
    return response.data.data;
  },

  /**
   * Create new account
   */
  async create(
    branchId: number,
    data: Omit<CreateAccountRequest, 'branch_id'>
  ): Promise<BranchAccount> {
    const response = await axios.post<ApiResponse<BranchAccount>>(
      `${BASE_URL}/branches/${branchId}/accounts`,
      data
    );
    
    if (!response.data.data) {
      throw new Error('Failed to create account');
    }
    
    return response.data.data;
  },

  /**
   * Update existing account
   */
  async update(
    accountId: number,
    data: UpdateAccountRequest
  ): Promise<BranchAccount> {
    const response = await axios.put<ApiResponse<BranchAccount>>(
      `${BASE_URL}/accounts/${accountId}`,
      data
    );
    
    if (!response.data.data) {
      throw new Error('Failed to update account');
    }
    
    return response.data.data;
  },

  /**
   * Delete account
   */
  async delete(accountId: number): Promise<void> {
    await axios.delete<ApiResponse<void>>(
      `${BASE_URL}/accounts/${accountId}`
    );
  },
};

// ============================================================================
// Business-Account Relationship Operations
// ============================================================================

export const accountBusinessAPI = {
  /**
   * Link account to business
   */
  async linkToBusiness(
    accountId: number,
    request: LinkAccountToBusinessRequest
  ): Promise<void> {
    await axios.post<ApiResponse<void>>(
      `${BASE_URL}/accounts/${accountId}/businesses`,
      request
    );
  },

  /**
   * Unlink account from business
   */
  async unlinkFromBusiness(
    accountId: number,
    businessId: number
  ): Promise<void> {
    await axios.delete<ApiResponse<void>>(
      `${BASE_URL}/accounts/${accountId}/businesses/${businessId}`
    );
  },

  /**
   * Set account as primary for business
   */
  async setPrimary(
    accountId: number,
    request: SetPrimaryAccountRequest
  ): Promise<void> {
    await axios.post<ApiResponse<void>>(
      `${BASE_URL}/accounts/${accountId}/businesses/primary`,
      request
    );
  },

  /**
   * Get all businesses linked to an account
   */
  async getLinkedBusinesses(accountId: number): Promise<LinkedBusiness[]> {
    const response = await axios.get<LinkedBusinessListResponse>(
      `${BASE_URL}/accounts/${accountId}/businesses`
    );
    
    return response.data.data;
  },
};

// ============================================================================
// Account-Branch Linking Operations (branch_account_links)
// ============================================================================

export interface LinkedBranch {
  branch_id: string;
  branch_name: string;
  branch_code: string | null;
  branch_type: string;
  is_active: boolean;
  is_main_branch: boolean;
  account_count: number;
}

export const accountBranchAPI = {
  /**
   * Get all branches linked to an account
   */
  async getLinkedBranches(accountId: number | string): Promise<LinkedBranch[]> {
    const response = await axios.get<{ success: boolean; data: LinkedBranch[] }>(
      `${BASE_URL}/accounts/${accountId}/branches`
    );
    return response.data.data;
  },

  /**
   * Link account to branch
   */
  async linkToBranch(accountId: number | string, branchId: string): Promise<void> {
    await axios.post<ApiResponse<void>>(
      `${BASE_URL}/accounts/${accountId}/branches`,
      { branch_id: branchId }
    );
  },

  /**
   * Unlink account from branch
   */
  async unlinkFromBranch(accountId: number | string, branchId: string): Promise<void> {
    await axios.delete<ApiResponse<void>>(
      `${BASE_URL}/accounts/${accountId}/branches/${branchId}`
    );
  },
};

// ============================================================================
// Configuration Operations
// ============================================================================

export const accountConfigAPI = {
  /**
   * Get account configuration (types, banks, validation rules)
   */
  async getConfig(): Promise<AccountConfigResponse['data']> {
    const response = await axios.get<AccountConfigResponse>(
      `${BASE_URL}/accounts/config`
    );
    
    return response.data.data;
  },
};

// ============================================================================
// Combined Export
// ============================================================================

export const accountAPI = {
  ...accountsAPI,
  business: accountBusinessAPI,
  config: accountConfigAPI,
};

export default accountAPI;
